import React from 'react';
import PropTypes from 'prop-types';

import { StyleSheet, css } from 'aphrodite';

import { backgroundGrey5, black, green, greyStroke, lighterGrey, white } from 'lib/css/colors';
import placeholderProductImage from 'images/placeholder-img-product-v2.svg';

function ImageGrid2({ images, selectedImages, setSelectedImages, isExpanded }) {
  const toggleSelect = id => {
    setSelectedImages(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id],
    );
  };

  if (!images || images.length === 0) {
    return <div className="text-center my-5">No images found.</div>;
  }
  return (
    <div
      className={css(styles.gridContainer, isExpanded ? styles.gridExpanded : styles.gridCollapsed)}
    >
      {images.map(image => (
        <div key={image.id} className={css(styles.imageWrapper)}>
          <div
            className={css(
              styles.imageContainer,
              selectedImages.includes(image.id) && styles.selected,
            )}
            onClick={() => toggleSelect(image.id)}
          >
            <img
              src={image.product_image.url || placeholderProductImage}
              className={css(styles.image)}
              onError={e => {
                e.target.onerror = null;
                e.target.src = placeholderProductImage;
              }}
              alt=""
            />
            <div className={css(styles.predictionLabel)}>
              <div className={css(styles.predictionText)}>
                <strong>Prediction:</strong>
                <div>{image.predicted_attribute.attribute_name || 'Unknown'}</div>
              </div>
              <div className={css(styles.predictionText)}>
                <strong>Confidence:</strong>
                <div>{image.confidence || 'Unknown'}</div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  gridContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    width: '100%',
    maxWidth: '100%',
    padding: '0',
    transition: 'width 0.3s ease, max-width 0.3s ease',
    boxSizing: 'border-box',
  },
  gridExpanded: {
    width: '100%',
    maxWidth: '100%',
  },
  gridCollapsed: {
    width: '100%',
    maxWidth: '100%',
  },
  imageWrapper: {
    width: '12.5%', // 8 images per row
    padding: '0 5px',
    marginBottom: '10px',
    boxSizing: 'border-box',
    '@media (max-width: 1600px)': {
      width: '14.28%', // 7 images per row
    },
    '@media (max-width: 1400px)': {
      width: '16.66%', // 6 images per row
    },
    '@media (max-width: 1200px)': {
      width: '20%', // 5 images per row
    },
    '@media (max-width: 992px)': {
      width: '25%', // 4 images per row
    },
    '@media (max-width: 768px)': {
      width: '33.33%', // 3 images per row
    },
    '@media (max-width: 576px)': {
      width: '50%', // 2 images per row
    },
  },
  imageContainer: {
    cursor: 'pointer',
    transition: 'transform 0.2s',
    height: '100%',
    border: `1px solid ${greyStroke}`,
    borderRadius: '4px',
    overflow: 'hidden',
    backgroundColor: white,
    display: 'flex',
    flexDirection: 'column',
    ':hover': {
      transform: 'scale(1.02)',
      boxShadow: `0 2px 5px rgba(${black}, 0.1)`,
    },
  },
  selected: {
    border: `2px solid ${green}`,
  },
  image: {
    width: '100%',
    height: '180px',
    objectFit: 'contain',
    padding: '2px',
    backgroundColor: white,
    flex: '1 0 auto',
  },
  predictionLabel: {
    padding: '8px 5px',
    fontSize: '12px',
    borderTop: `1px solid ${lighterGrey}`,
    backgroundColor: backgroundGrey5,
    textAlign: 'center',
    overflow: 'hidden',
    flex: '0 0 auto',
    minHeight: '45px',
  },
  predictionText: {
    overflow: 'hidden',
    wordWrap: 'break-word',
    lineHeight: '1.2',
  },
});

ImageGrid2.propTypes = {
  images: PropTypes.array.isRequired,
  selectedImages: PropTypes.array.isRequired,
  setSelectedImages: PropTypes.func.isRequired,
  isExpanded: PropTypes.bool.isRequired,
};

export default ImageGrid2;
